import React, { useState, useEffect, useMemo, memo } from 'react'
import { FaBrain, FaTasks, FaStar, FaLock, FaClipboardList, FaCheck, FaRegCircle } from 'react-icons/fa'
import { Heading, Text, Stack, Icon, Button, Input, Grid, Radio, RadioGroup, Checkbox, Box, Badge, Alert, SimpleAccordion, SimpleModal, SimpleTable, Wrap, FormControl, Card } from '@/components/lib'
import DataTypeConfiguration, { useDataTypeConfiguration } from './DataTypeConfiguration'
import utils from '../../utils/utils'
import api from '../../api'
import useStore from '../../store'

// The settings that belong to a chosen format live in DataTypeConfiguration so
// the stage's field editor can offer them too. Re-exported here because these
// lists were part of this module's interface before that move.
export { likertPresets, times, nutritionTypes } from './DataTypeConfiguration'

export const categories = [
  {
    name: 'Essential',
    description: 'Basic data inputs, including text, numeric, sliders, and multiple choice.',
    icon: FaTasks,
    feature: 'basicDataTypes',
    types: [
      { key: 'text', mobile: true, label: 'Short text', description: 'For collecting a short piece of text (e.g. a word, phrase or a sentence).' },
      { key: 'paragraph', mobile: true, label: 'Long text', description: 'For collecting a paragraph or more of text.' },
      { key: 'choice', mobile: true, label: 'Choice', description: 'Allow participants to select one or more options from a selection.' },
      { key: 'integer', mobile: true, label: 'Integer', description: 'For collecting a single integer value (e.g. 42).' },
      { key: 'float', mobile: true, label: 'Decimal number', description: 'For collecting a single floating point number (e.g. 276.42). We refer to this internally as a "float".' },
      { key: 'slider', mobile: true, label: 'Slider', description: 'For collecting a single integer value on a range between a specified minimum and maximum (e.g. between 1 and 10).' },
      // `mobile` drives the "Participant compatible" badge only, as for the
      // Likert below — flip to true once the app build has cleared review.
      { key: 'rating', mobile: false, label: 'Rating', description: 'For rating one thing on a single scale of three to six points, drawn as stars, numbers or smileys (e.g. one to five stars).' },
      // `mobile` drives the "Participant compatible" badge only. The field is
      // implemented in the participant app, but the badge stays off until that
      // build has been through app review — flip to true once it has shipped.
      { key: 'likert', mobile: false, label: 'Likert matrix', description: 'For rating several statements on one shared scale (e.g. a set of statements each answered from "Strongly disagree" to "Strongly agree").' },
      { key: 'date', mobile: true, label: 'Date', description: 'For collecting a point in time represented by a date (e.g. a date of birth). You do not need to record the date of submission since Trialflare will handle this for you.' },
      { key: 'datetime', mobile: true, label: 'Date & time', description: 'For collecting a point in time represented by a date and time (e.g. the time the participant used a product). You do not need to record the date of submission since Trialflare will handle this for you.' },
      { key: 'time', mobile: true, label: 'Time', description: 'For collecting a time of day only (e.g. wake-up time, medication time). No date is stored.' }
    ]
  },
  {
    name: 'Advanced',
    description: 'More complex data types.',
    icon: FaStar,
    types: [
      { key: 'temperature', mobile: true, label: 'Temperature', description: 'For collecting a temperature measurement.', feature: 'basicDataTypes' },
      { key: 'weight', mobile: true, label: 'Weight', description: 'For collecting a weight measurement.', feature: 'basicDataTypes' },
      { key: 'height', mobile: true, label: 'Height', description: 'For collecting a height measurement.', feature: 'basicDataTypes' },
      { key: 'bmi', mobile: true, label: 'BMI', description: 'For collecting a person\'s height and weight and calculating their body-mass index.', feature: 'basicDataTypes' },
      { key: 'file', mobile: false, label: 'File', description: 'For allowing the upload and collection of files, such as PDFs, spreadsheets, documents, and more.', feature: 'fileDataType' },
      { key: 'image', mobile: true, label: 'Image', description: 'For allowing the upload and collection of image files. On mobile, this allows the participant to use the camera.', feature: 'imageDataType' },
      { key: 'video', mobile: true, label: 'Video', description: 'For allowing the upload and collection of video files.', feature: 'videoDataType' },
      { key: 'food', mobile: true, label: 'Nutrition input', description: 'Enter a food or drink item or scan a barcode.', feature: 'foodDataType' },
      { key: 'calculation', mobile: true, label: 'Calculation', description: 'A computed value from a formula.', feature: 'calculationDataType' },
      { key: 'teethChart', mobile: false, label: 'Teeth chart', description: 'For collecting dental chart annotations including conditions, surfaces, and clinical notes for each tooth.', feature: 'teethChart' },
      { key: 'table', mobile: false, label: 'Table', description: 'For collecting several rows of structured data, with columns you define.', feature: 'tableDataType' }
    ]
  },
  {
    name: 'Cognition',
    description: 'A suite of tests for measuring various cognitive capabilities.',
    icon: FaBrain,
    feature: 'cognitionDataTypes',
    types: [
      { key: 'stroop', mobile: true, label: 'Stroop', description: 'For running and collecting data from a Stroop test.' },
      { key: 'pal', mobile: true, label: 'PAL', description: 'For running and collecting data from a paired associates learning test.' },
      { key: 'ravlt', mobile: false, label: 'RAVLT', description: 'For running and collecting data from a Rey Auditory Verbal Learning Test.' }
    ]
  },
  {
    name: 'PROMIS',
    description: 'NIH PROMIS validated patient-reported outcome questionnaires.',
    icon: FaClipboardList,
    feature: 'promis',
    types: [
      { key: 'promisForm', mobile: false, label: 'PROMIS Form', description: 'A validated NIH PROMIS questionnaire administered via the Assessment Center.' },
      { key: 'promisBattery', mobile: false, label: 'PROMIS Battery', description: 'A collection of PROMIS forms administered sequentially as a single battery.' }
    ]
  }
]

function NewData ({ trigger, trial, dataTypeId, onComplete, isOpen, onClose }) {
  const [internalOpen, setInternalOpen] = useState(false)
  // Controlled when `isOpen` is supplied, so one editor can be mounted once for
  // a whole table and pointed at whichever row was opened — rather than a
  // hidden modal, with all its state, per row.
  const isControlled = isOpen !== undefined
  const open = isControlled ? isOpen : internalOpen
  const setOpen = next => {
    if (!isControlled) return setInternalOpen(next)
    if (!next) onClose?.()
  }
  const [dataType, setDataType] = useState()
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState()
  const [error, setError] = useState()
  const [saving, setSaving] = useState(false)
  const [selectedType, setSelectedType] = useState('text')
  const [name, setName] = useState('')
  const [nickname, setNickname] = useState('')
  const [isRestricted, setIsRestricted] = useState(false)
  const [isPii, setIsPii] = useState(false)
  const [promisQuery, setPromisQuery] = useState('')
  const [promisForms, setPromisForms] = useState([])
  const [promisFormsLoading, setPromisFormsLoading] = useState(false)
  const [promisBatteryQuery, setPromisBatteryQuery] = useState('')
  const [promisBatteries, setPromisBatteries] = useState([])
  const [promisBatteriesLoading, setPromisBatteriesLoading] = useState(false)
  const config = useDataTypeConfiguration()
  const { team, updateTrial, addDataType } = useStore()
  const isEditing = !!dataTypeId

  useEffect(() => {
    if (!dataTypeId || !open) return
    setDataType()
    setLoadError()
    setLoading(true)
    api.dataTypes.get(dataTypeId, dataType => {
      setDataType(dataType)
      setLoading(false)
    }, err => {
      setLoading(false)
      setLoadError(err.message)
    })
  }, [dataTypeId, open])

  useEffect(() => {
    if (open && utils.hasFeature(team, 'promis') && !promisForms.length) {
      setPromisFormsLoading(true)
      api.promis.listForms(({ forms }) => {
        setPromisForms(forms || [])
        setPromisFormsLoading(false)
      }, () => setPromisFormsLoading(false))
    }
    if (open && utils.hasFeature(team, 'promis') && !promisBatteries.length) {
      setPromisBatteriesLoading(true)
      api.promis.listBatteries(({ batteries }) => {
        setPromisBatteries(batteries || [])
        setPromisBatteriesLoading(false)
      }, () => setPromisBatteriesLoading(false))
    }
  }, [open])

  useEffect(() => {
    if (dataType?.name) {
      setName(dataType.name)
      setNickname(dataType.nickname)
      setIsRestricted(dataType.isRestricted || false)
      setIsPii(dataType.isPii || false)
      setSelectedType(dataType.type)
      config.load(dataType)
    }
  }, [dataType])

  function complete (dataType) {
    if (!dataTypeId) {
      setName('')
      setNickname('')
      setIsRestricted(false)
      setIsPii(false)
      setPromisQuery('')
      setPromisBatteryQuery('')
      config.reset()
    }
    setOpen(false)
    if (onComplete) onComplete(dataType)
  }

  function create (customCallback) {
    setError()
    if (isEditing && !dataType?._id) {
      return setError(loading
        ? 'This data type is still loading'
        : 'We could not load this data type')
    }
    const { data: typeData, error: configError } = config.build(selectedType)
    if (configError) return setError(configError)
    const data = { name, nickname, isRestricted, isPii, type: selectedType, ...typeData }
    setSaving(true)
    if (isEditing) {
      api.dataTypes.update(dataType._id, data, dataType => {
        utils.toast('success', 'Data type saved')
        addDataType(dataType)
        customCallback ? customCallback(dataType) : complete(dataType)
        setSaving(false)
      }, err => { setSaving(false); setError(err.message) })
    } else {
      api.trials.createDataType(trial._id, data, dataType => {
        addDataType(dataType)
        updateTrial(trial._id, { dataTypeCount: (trial.dataTypeCount ?? 1) + 1 })
        utils.toast('success', 'Data type created')
        customCallback ? customCallback(dataType) : complete(dataType)
        setSaving(false)
      }, err => { setSaving(false); setError(err.message) })
    }
  }

  return (
    <>
      {isControlled
        ? null
        : trigger
          ? React.cloneElement(trigger, { onClick: () => setOpen(true) })
          : <Button colorPalette='primary' onClick={() => setOpen(true)}>Create new data type (variable)</Button>}
      <SimpleModal
        isOpen={open} onClose={() => setOpen(false)} size='lg'
        header={isEditing ? 'Edit your data type (variable)' : 'Create a new data type (variable)'}
        body={
          <Stack gap={5}>
            {loadError &&
              <Alert status='error' variant='left-accent' title='There was a problem loading your data type'>
                {loadError}. Please close this window and try again.
              </Alert>}
            <Card gap={5}>
              <Stack>
                <Heading as='h3' size='md'>1. About your data type</Heading>

                <FormControl label='Name' helperText={'This helps you identify the data type. It\'s not shown to participants.'}>
                  <Input placeholder='My data type' type='text' value={name} onChange={e => setName(e.target.value)} />
                </FormControl>
              </Stack>

              <FormControl label='Nickname' helperText='Optionally, provide a more human-friendly nickname for this data type.'>
                <Input placeholder='My nickname' type='text' value={nickname} onChange={e => setNickname(e.target.value)} />
              </FormControl>

              <FormControl helperText='Study data recorded for restricted types is only available to users with the appropriate permissions.'>
                <Checkbox isChecked={isRestricted || false} onChange={e => setIsRestricted(e.checked)}>Restrict this data type</Checkbox>
              </FormControl>

              <FormControl label='PII data type' helperText='Mark this data type as containing personally identifiable information. Only users with the PII study data permission can view responses for this field.'>
                <Checkbox isChecked={isPii || false} onChange={e => setIsPii(e.checked)}>This data type contains PII</Checkbox>
              </FormControl>
            </Card>

            <Card gap={3}>
              <Box display='flex' justifyContent='space-between'>
                <Heading as='h3' size='md'>2. Choose a format</Heading>
                {selectedType && <Badge colorPalette='blue' size='lg'>{selectedType}</Badge>}
              </Box>
              <Text>Select a measurement from the data type library.</Text>
              <SimpleAccordion
                items={[
                  ...categories
                    .filter(c => c.feature !== 'promis')
                    .map(c => ({
                      key: c.name,
                      label: (
                        <Box flex='1' textAlign='left'>
                          <Heading as='h4' size='sm' color='primary.500'><Box as={c.icon} mr={2} display='inline-block' /> {c.name}</Heading>
                          <Text>{c.description}</Text>
                        </Box>),
                      content: (
                        <RadioGroup value={selectedType} colorPalette='primary'>
                          <Grid templateColumns={{ md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }} gap={2}>
                            {c.types.map(t => {
                              const allowed = utils.hasFeature(team, t.feature || c.feature)
                              return (
                                <Stack key={t.key} p={3} borderWidth='1px' bg={selectedType === t.key ? 'primary.muted' : 'bg.surface'} onClick={(e, r) => setSelectedType(t.key)} cursor='pointer'>
                                  {!allowed && <Box><Badge colorPalette='orange' size='sm'><FaLock /> Not available</Badge></Box>}
                                  <Radio value={t.key} disabled={!allowed} name='type' checked={selectedType === t.key}>
                                    <Heading as='h4' size='sm' fontWeight='bold'>{t.label}</Heading>
                                  </Radio>
                                  <Wrap gap={1}>
                                    <Badge colorPalette='gray' size='sm'>{t.key}</Badge>
                                    {t.mobile && <Badge colorPalette='green' size='sm'>Participant compatible</Badge>}
                                  </Wrap>
                                  <Text fontSize='xs'>{t.description}</Text>
                                </Stack>
                              )
                            })}
                          </Grid>
                        </RadioGroup>)
                    })),
                  ...(utils.hasFeature(team, 'promis')
                    ? [{
                        key: 'PROMIS',
                        label: (
                          <Box flex='1' textAlign='left'>
                            <Heading as='h4' size='sm' color='primary.500'><Box as={FaClipboardList} mr={2} display='inline-block' /> PROMIS Forms</Heading>
                            <Text>NIH PROMIS validated patient-reported outcome questionnaires.</Text>
                          </Box>),
                        content: (
                          <PromisFormPicker
                            promisForms={promisForms}
                            promisFormsLoading={promisFormsLoading}
                            promisFormOid={config.promisFormOid}
                            setPromisFormOid={config.setPromisFormOid}
                            promisQuery={promisQuery}
                            setPromisQuery={setPromisQuery}
                            setSelectedType={setSelectedType}
                          />
                        )
                      }, {
                        key: 'PROMIS_BATTERY',
                        label: (
                          <Box flex='1' textAlign='left'>
                            <Heading as='h4' size='sm' color='primary.500'><Box as={FaClipboardList} mr={2} display='inline-block' /> PROMIS Batteries</Heading>
                            <Text>Collections of PROMIS forms administered together as a single battery.</Text>
                          </Box>),
                        content: (
                          <PromisBatteryPicker
                            promisBatteries={promisBatteries}
                            promisBatteriesLoading={promisBatteriesLoading}
                            promisBatteryOid={config.promisBatteryOid}
                            setPromisBatteryOid={config.setPromisBatteryOid}
                            promisBatteryQuery={promisBatteryQuery}
                            setPromisBatteryQuery={setPromisBatteryQuery}
                            setSelectedType={setSelectedType}
                          />
                        )
                      }]
                    : [])
                ]}
              />
            </Card>

            <DataTypeConfiguration
              config={config} selectedType={selectedType} isEditing={isEditing}
              dataType={dataType} headingPrefix='3. ' saveDataType={create}
            />

            <Stack bg='bg.subtle' p={3} direction='row' shadow='md' rounded='md' justifyContent='space-between' alignItems='center' position='sticky' bottom='10px'>
              <Box>
                {error &&
                  <Alert status='error' variant='left-accent' title='There was a problem saving your data type'>
                    {error}. Please fix these issues and try again.
                  </Alert>}
              </Box>
              {dataType && dataType?.type !== selectedType &&
                <Alert size='sm' status='warning'>Changing the format of this data type can have unexpected side effects. Where possible, we recommend creating a new data type.</Alert>}
              <Stack direction='row'>
                <Button variant='ghost' onClick={() => setOpen(false)}>Cancel</Button>
                <Button colorPalette='primary' onClick={() => create()} isLoading={saving || (isEditing && loading)} disabled={isEditing && !dataType}>{isEditing ? 'Save data type' : 'Create data type'}</Button>
              </Stack>
            </Stack>
          </Stack>
}
      />
    </>
  )
}

const PromisFormPicker = memo(function PromisFormPicker ({
  promisForms,
  promisFormsLoading,
  promisFormOid,
  setPromisFormOid,
  promisQuery,
  setPromisQuery,
  setSelectedType
}) {
  const normalisedQuery = useMemo(
    () => promisQuery.trim().toLowerCase(),
    [promisQuery]
  )

  const filteredPromisForms = useMemo(
    () => normalisedQuery
      ? promisForms.filter(f =>
        f.Name?.toLowerCase().includes(normalisedQuery) ||
          f.OID?.toLowerCase().includes(normalisedQuery))
      : promisForms,
    [promisForms, normalisedQuery]
  )

  const rows = useMemo(
    () => filteredPromisForms.map(f => [
      <Icon
        key={`${f.OID}-icon`}
        color={promisFormOid === f.OID ? 'green.500' : 'fg.subtle'}
        cursor='pointer'
        onClick={() => { setSelectedType('promisForm'); setPromisFormOid(f.OID) }}
      >
        {promisFormOid === f.OID ? <FaCheck /> : <FaRegCircle />}
      </Icon>,
      <Text
        key={`${f.OID}-name`}
        fontSize='sm'
        cursor='pointer'
        fontWeight={promisFormOid === f.OID ? 'bold' : 'normal'}
        onClick={() => { setSelectedType('promisForm'); setPromisFormOid(f.OID) }}
      >
        {f.Name}
      </Text>,
      <Badge key={`${f.OID}-oid`} colorPalette='gray' size='xs'>{f.OID}</Badge>
    ]),
    [filteredPromisForms, promisFormOid, setPromisFormOid, setSelectedType]
  )

  return (
    <Box>
      <Input
        size='sm' mb={3} bg='bg.surface'
        placeholder='Search forms by name or OID…'
        value={promisQuery}
        onChange={e => setPromisQuery(e.target.value)}
      />
      {promisFormsLoading
        ? <Text fontSize='sm' color='fg.muted'>Loading PROMIS forms…</Text>
        : filteredPromisForms.length === 0
          ? <Text fontSize='sm' color='fg.muted'>{promisQuery ? 'No forms match your search.' : 'No PROMIS forms available.'}</Text>
          : (
            <Box maxH='300px' overflowY='auto'>
              <SimpleTable
                tableProps={{ size: 'sm' }}
                headers={['', 'Name', 'OID']}
                rows={rows}
              />
            </Box>
            )}
    </Box>
  )
})

const PromisBatteryPicker = memo(function PromisBatteryPicker ({
  promisBatteries,
  promisBatteriesLoading,
  promisBatteryOid,
  setPromisBatteryOid,
  promisBatteryQuery,
  setPromisBatteryQuery,
  setSelectedType
}) {
  const normalisedQuery = useMemo(
    () => promisBatteryQuery.trim().toLowerCase(),
    [promisBatteryQuery]
  )

  const filteredBatteries = useMemo(
    () => normalisedQuery
      ? promisBatteries.filter(b =>
        b.Name?.toLowerCase().includes(normalisedQuery) ||
          b.OID?.toLowerCase().includes(normalisedQuery))
      : promisBatteries,
    [promisBatteries, normalisedQuery]
  )

  const rows = useMemo(
    () => filteredBatteries.map(b => [
      <Icon
        key={`${b.OID}-icon`}
        color={promisBatteryOid === b.OID ? 'green.500' : 'fg.subtle'}
        cursor='pointer'
        onClick={() => { setSelectedType('promisBattery'); setPromisBatteryOid(b.OID) }}
      >
        {promisBatteryOid === b.OID ? <FaCheck /> : <FaRegCircle />}
      </Icon>,
      <Text
        key={`${b.OID}-name`}
        fontSize='sm'
        cursor='pointer'
        fontWeight={promisBatteryOid === b.OID ? 'bold' : 'normal'}
        onClick={() => { setSelectedType('promisBattery'); setPromisBatteryOid(b.OID) }}
      >
        {b.Name}
      </Text>,
      <Badge key={`${b.OID}-oid`} colorPalette='gray' size='xs'>{b.OID}</Badge>
    ]),
    [filteredBatteries, promisBatteryOid, setPromisBatteryOid, setSelectedType]
  )

  return (
    <Box>
      <Input
        size='sm' mb={3} bg='bg.surface'
        placeholder='Search batteries by name or OID…'
        value={promisBatteryQuery}
        onChange={e => setPromisBatteryQuery(e.target.value)}
      />
      {promisBatteriesLoading
        ? <Text fontSize='sm' color='fg.muted'>Loading PROMIS batteries…</Text>
        : filteredBatteries.length === 0
          ? <Text fontSize='sm' color='fg.muted'>{promisBatteryQuery ? 'No batteries match your search.' : 'No PROMIS batteries available.'}</Text>
          : (
            <Box maxH='300px' overflowY='auto'>
              <SimpleTable
                tableProps={{ size: 'sm' }}
                headers={['', 'Name', 'OID']}
                rows={rows}
              />
            </Box>
            )}
    </Box>
  )
})

export default NewData
